<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-input-field
    type="date"
    v-bind="forwardedAttrs"
    :placeholder="userDateFormat"
    :day-attributes="attributes"
    :events="calendarEvents"
    :display-format="jsDateFormat"
    :locale="locale"
    @select-year="onSelectYear"
    @update:model-value="onUpdateModelValue"
  />
</template>

<script>
import {computed, onBeforeMount, reactive, toRefs, useAttrs, watch} from 'vue';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  startOfYear,
  endOfYear,
  formatDate,
  parseDate,
} from '@ohrm/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';

export default {
  name: 'DateInput',
  inheritAttrs: false,
  props: {
    minSelectableDate: {
      type: [String, Date],
      required: false,
      default: null,
    },
    disabledDates: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, {emit}) {
    const state = reactive({
      attributes: [],
      holidayEvents: [],
      disabledEvents: [],
    });
    const attrs = useAttrs();
    const forwardedAttrs = computed(() => {
      // Avoid passing the parent's v-model listener straight through,
      // because we intercept it to enforce minSelectableDate.
      // Vue uses camelCase listener key in attrs.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {['onUpdate:modelValue']: _ignored, ...rest} = attrs;
      return rest;
    });

    const http = new APIService(window.appGlobal.baseUrl, '');
    const {jsDateFormat, userDateFormat} = useDateFormat();
    const {locale} = useLocale();

    const normalizeToDate = (value) => {
      if (!value) return null;
      if (value instanceof Date && !isNaN(value.valueOf())) {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d;
      }
      const valueStr = String(value);
      const parsed =
        parseDate(valueStr, 'yyyy-MM-dd') || parseDate(valueStr, jsDateFormat);
      if (parsed) {
        parsed.setHours(0, 0, 0, 0);
        return parsed;
      }
      const nativeParsed = new Date(valueStr);
      if (!isNaN(nativeParsed.valueOf())) {
        nativeParsed.setHours(0, 0, 0, 0);
        return nativeParsed;
      }
      return null;
    };

    const minDate = computed(() => normalizeToDate(props.minSelectableDate));

    const toISODate = (date) => {
      if (!date) return null;
      try {
        return formatDate(date, 'yyyy-MM-dd');
      } catch (e) {
        return null;
      }
    };

    const explicitDisabledSet = computed(() => {
      const set = new Set();
      (props.disabledDates || []).forEach((value) => {
        const d = normalizeToDate(value);
        const key = d ? toISODate(d) : null;
        if (key) set.add(key);
      });
      return set;
    });

    const onUpdateModelValue = (value) => {
      const selected = normalizeToDate(value);
      if (minDate.value && selected && selected < minDate.value) {
        // Ignore selection: makes dates before minSelectableDate effectively not pickable.
        return;
      }

      if (selected) {
        const key = toISODate(selected);
        if (key && explicitDisabledSet.value.has(key)) {
          // Ignore selection: explicitly disabled dates.
          return;
        }
      }
      emit('update:modelValue', value);
    };

    const responseValidator = (status) => {
      return (status >= 200 && status < 300) || status === 403;
    };

    const calendarEvents = computed(() => {
      // Put disabled events first so they take precedence over holiday styling
      return [...state.disabledEvents, ...state.holidayEvents];
    });

    const fetchWorkWeek = async () => {
      http
        .request({
          method: 'GET',
          url: '/api/v2/leave/workweek',
          params: {
            model: 'indexed',
          },
          validateStatus: responseValidator,
        })
        .then(({data}) => {
          if (data?.data) {
            state.attributes = Object.keys(data.data).map((i) => {
              return {
                index: parseInt(i),
                class:
                  data.data[i] === 8
                    ? '--non-working-day'
                    : data.data[i] === 4
                    ? '--working-day-half'
                    : '',
              };
            });
          }
        });
    };

    const fetchEvents = async (fromDate, toDate) => {
      http
        .request({
          method: 'GET',
          url: '/api/v2/leave/holidays',
          params: {
            fromDate,
            toDate,
          },
          validateStatus: responseValidator,
        })
        .then(({data}) => {
          if (Array.isArray(data?.data)) {
            state.holidayEvents = data.data.map((event) => {
              return {
                date: parseDate(event.date, 'yyyy-MM-dd'),
                type: event.name,
                class: event.length === 0 ? '--holiday-full' : '--holiday-half',
              };
            });
          }
        });
    };

    const refreshDisabledEvents = () => {
      const eventsByKey = new Map();

      // Disabled events for minSelectableDate: from today to (min - 1)
      const min = minDate.value;
      if (min) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const end = new Date(min);
        end.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() - 1);

        if (end >= today) {
          const cur = new Date(today);
          while (cur <= end) {
            const key = toISODate(cur);
            if (key && !eventsByKey.has(key)) {
              eventsByKey.set(key, {
                date: new Date(cur),
                type: 'disabled',
                class: '--date-disabled',
                style: {
                  opacity: '0.35',
                  cursor: 'not-allowed',
                },
              });
            }
            cur.setDate(cur.getDate() + 1);
          }
        }
      }

      // Explicit disabled dates
      explicitDisabledSet.value.forEach((key) => {
        if (eventsByKey.has(key)) return;
        const d = normalizeToDate(key);
        if (!d) return;
        eventsByKey.set(key, {
          date: d,
          type: 'disabled',
          class: '--date-disabled',
          style: {
            opacity: '0.35',
            cursor: 'not-allowed',
          },
        });
      });

      state.disabledEvents = Array.from(eventsByKey.values());
    };

    const onSelectYear = async ({year}) => {
      const now = new Date().setFullYear(year);
      const fromDate = formatDate(startOfYear(now), 'yyyy-MM-dd');
      const endDate = formatDate(endOfYear(now), 'yyyy-MM-dd');
      fetchEvents(fromDate, endDate);
    };

    onBeforeMount(async () => {
      await fetchWorkWeek();
      await onSelectYear({year: new Date().getFullYear()});
      refreshDisabledEvents();
    });

    watch([minDate, explicitDisabledSet], () => {
      refreshDisabledEvents();
    });

    return {
      jsDateFormat,
      userDateFormat,
      ...toRefs(state),
      onSelectYear,
      locale,
      forwardedAttrs,
      onUpdateModelValue,
      calendarEvents,
    };
  },
};
</script>
